import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import AddressSelectorFields from '../Common/AddressSelectorFields';
import DocumentUpload from '../Common/DocumentUpload';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import { useAuth } from '../../Context/AuthContext';
import { getAllProviderSettings } from '../../Services/ProviderService';
import CustomDatePicker from '../Common/CustomDatePicker';
import CustomPhoneInput from '../Common/CustomPhoneInput';

const SETTING_TYPE_IDS = {
  employer_type: 26,
  employer_in: 27,
};

const mapProviderSettingItem = (item) => ({
  value: item.id ?? item.Id ?? item.value ?? item.value_id ?? null,
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
});

const validationSchema = Yup.object({
  employer_name: Yup.string().trim().nullable(),
  is_current_employed: Yup.boolean().nullable(),
  practice_type: Yup.number().nullable(),
  position_held: Yup.string().trim().nullable(),
  position_held_type: Yup.number().nullable(),
  start_date: Yup.date().nullable().typeError('Invalid date format'),
  end_date: Yup.date().nullable().typeError('Invalid date format'),
  employer_in: Yup.number().nullable(),
  reason_for_leaving: Yup.string().trim().nullable(),
  document: Yup.mixed().nullable(),
  contact: Yup.object({
    contact_name: Yup.string().trim().nullable(),
    contact_title: Yup.string().trim().nullable(),
    contact_email: Yup.string().email('Invalid email').nullable(),
    contact_phone: Yup.string().trim().nullable(),
    contact_fax: Yup.string().trim().nullable(),
  }).nullable(),
  address: Yup.object({
    street: Yup.string().trim().nullable(),
    street2: Yup.string().trim().nullable(),
    city: Yup.string().trim().nullable(),
    state: Yup.string().trim().nullable(),
    country: Yup.string().trim().nullable(),
    zipcode: Yup.string().trim().nullable(),
  }).nullable(),
});

const emptyForm = {
  id: null,
  provider_id: null,
  status_id: null,
  file_id: null,
  employer_name: '',
  is_current_employed: '',
  practice_type: null,
  position_held: '',
  position_held_type: null,
  start_date: null,
  end_date: null,
  employer_in: null,
  reason_for_leaving: '',
  document: null,
  address: {
    street: '',
    street2: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
  },
  contact: {
    contact_name: '',
    contact_title: '',
    contact_email: '',
    contact_phone: '',
    contact_fax: '',
  },
  
};

export default function WorkHistoryModal({
  isOpen,
  title,
  initialData,
  onClose,
  onSave,
  isLoading,
}) {
  const { token } = useAuth();
  const [providerSettings, setProviderSettings] = useState({
    employer_type: [],
    employer_in: [],
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  const loadProviderSettings = async () => {
    if (!token) return;

    setSettingsLoading(true);
    try {
      const entries = Object.entries(SETTING_TYPE_IDS);
      const results = await Promise.all(
        entries.map(async ([key, typeId]) => {
          try {
            const response = await getAllProviderSettings(token, typeId);
            if (response?.is_success) {
              return {
                key,
                options: Array.isArray(response.data) ? response.data.map(mapProviderSettingItem) : [],
              };
            }else if (response?.is_success === false && response?.status_code === 500) {
              toast.error('Internal server error. Please contact support team.');
            }
          } catch (error) {
            console.error(`Failed to load ${key} settings:`, error);
          }
          return { key, options: [] };
        })
      );

      setProviderSettings(results.reduce((acc, item) => ({ ...acc, [item.key]: item.options }), {}));
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadProviderSettings();
  }, [token]);

  const initialValues = useMemo(() => {
    if (!initialData) return emptyForm;

    const normalized = {
      ...emptyForm,
      ...initialData,
      is_current_employed:
        initialData.is_current_employed ??
        initialData.is_currently_employed ??
        emptyForm.is_current_employed,
      document:
        initialData.document || initialData.file_name || initialData.fileName || null,
      address: { ...emptyForm.address, ...(initialData.address || {}) },
      contact: { ...emptyForm.contact, ...(initialData.contact || {}) },
    };

    return normalized;
  }, [initialData]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        id: values.id || null,
        practice_type: values.practice_type !== '' && values.practice_type !== null && values.practice_type !== undefined ? Number(values.practice_type) : null,
        position_held_type: values.position_held_type !== '' && values.position_held_type !== null && values.position_held_type !== undefined ? Number(values.position_held_type) : null,
        employer_in: values.employer_in !== '' && values.employer_in !== null && values.employer_in !== undefined ? Number(values.employer_in) : null,
        status_id: values.status_id !== '' && values.status_id !== null && values.status_id !== undefined ? Number(values.status_id) : null,
        is_current_employed: values.is_current_employed === true || values.is_current_employed === 'true' ? true : values.is_current_employed === false || values.is_current_employed === 'false' ? false : null,
        start_date: values.start_date || null,
        end_date: values.end_date || null,
        type: values.id ? 'update' : 'create',
      };
      onSave(payload);
    },
  });

  if (!isOpen) return null;

  const handleAddressFieldChange = (fieldName, value) => {
    formik.setFieldValue(fieldName, value);
  };

  const handleSelectChange = (fieldName) => (value) => {
    const parsedValue = value === '' || value === null ? null : Number(value);
    formik.setFieldValue(fieldName, parsedValue);
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 rounded-4">
          <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold hc-text-primary m-0">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body px-4 py-2">
              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-semibold hc-text-primary mb-3">Employer Details</h6>
                <div className="row g-3 align-items-center">
                  <div className="col-md-12 col-lg-8">
                    <label className="form-label fw-semibold">Practice/Employer Name</label>
                    <input
                      {...formik.getFieldProps('employer_name')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter practice/employer name"
                    />
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">Currently Employed</label>
                    <select
                      name="is_current_employed"
                      value={formik.values.is_current_employed === true ? 'true' : formik.values.is_current_employed === false ? 'false' : ''}
                      onChange={(event) => formik.setFieldValue('is_current_employed', event.target.value === 'true')}
                      className="form-select rounded-3 shadow-sm"
                    >
                      <option value="">Select Option</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <SearchableDropdown
                      label="Practice/Employer Type"
                      placeholder="Select Option"
                      options={providerSettings.employer_type}
                      value={formik.values.practice_type}
                      onChange={handleSelectChange('practice_type')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No employer types found"
                    />
                  </div>

                  <div className="col-md-12 col-lg-8">
                    <label className="form-label fw-semibold">Position Held</label>
                    <input
                      {...formik.getFieldProps('position_held')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter position held"
                    />
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">Start Date</label>
                    <CustomDatePicker
                      name="start_date"
                      formik={formik}
                    />
                    {/* <input
                      type="date"
                      {...formik.getFieldProps('start_date')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">End Date</label>
                      <CustomDatePicker
                      name="end_date"
                      formik={formik}
                    />

                    {/* <input
                      type="date"
                      {...formik.getFieldProps('end_date')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>
                  <div className="col-md-12 col-lg-4">
                    <SearchableDropdown
                      label="List this Practice/Employer in"
                      placeholder="Select Option"
                      options={providerSettings.employer_in}
                      value={formik.values.employer_in}
                      onChange={handleSelectChange('employer_in')}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      emptyMessage="No listing options found"
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Reason For Leaving</label>
                    <textarea
                      {...formik.getFieldProps('reason_for_leaving')}
                      className="form-control rounded-3 shadow-sm"
                      rows={3}
                      placeholder="Enter reason for leaving"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Document</h6>
                <DocumentUpload
                  id="work-history-document-input"
                  label="Upload Document"
                  value={formik.values.document}
                  onChange={(file) => formik.setFieldValue('document', file)}
                />
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Contact Information</h6>
                <div className="row g-3">
                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">Contact Name</label>
                    <input
                      {...formik.getFieldProps('contact.contact_name')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter contact name"
                    />
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">Contact Title</label>
                    <input
                      {...formik.getFieldProps('contact.contact_title')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter contact title"
                    />
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">Contact Email</label>
                    <input
                      type="email"
                      {...formik.getFieldProps('contact.contact_email')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter contact email"
                    />
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">Contact Phone</label>
                    {/* <CustomPhoneInput
                      name="contact.contact_phone"
                      {...formik.getFieldProps('contact.contact_phone')}
                       formik={formik}
                        defaultCountry="us"
                    /> */}
                    <input
                       name="contact.contact_phone"
                      {...formik.getFieldProps('contact.contact_phone')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter contact phone"
                    />
                  </div>

                  <div className="col-md-12 col-lg-4">
                    <label className="form-label fw-semibold">Contact Fax</label>
                    <input
                      {...formik.getFieldProps('contact.contact_fax')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter contact fax"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Address Information</h6>
                <div className="row g-3">
                  <div className="col-12">
                    <AddressSelectorFields
                      title="Address"
                      fieldPrefix="address"
                      values={formik.values.address}
                      errors={formik.errors.address || {}}
                      touched={formik.touched.address || {}}
                      onFieldChange={handleAddressFieldChange}
                      disabled={false}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light rounded-3 px-4" onClick={onClose} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn hc-bg-primary text-white rounded-3 px-4 d-flex align-items-center justify-content-center gap-2" disabled={isLoading} aria-busy={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
